import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useAit } from '../../../context/AitContext';
import {
  ArrowLeft, Loader2, Plus, ArrowLeftRight, TrendingDown, TrendingUp,
  CheckCircle2, AlertTriangle, XCircle, Camera, Save, Trash2, Sparkles, RefreshCw, Calendar,
  AlertOctagon, ChevronDown, ChevronUp, ShieldAlert, Pencil, Clock, Search, FileUp, Eye, FlaskConical, RotateCcw,
  Accessibility, Ear, Share2, Download, Copy, Link, MoreVertical, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import AitChatDrawer from './AitChatDrawer';
import AitMonthlyGrid from './AitMonthlyGrid';
import StatusChangePopover, { getRelevantDateFields } from './StatusChangePopover';
import AitHistoryTimeline from './AitHistoryTimeline';

const DESIGNATIONS = [20, 30, 40, 50, 60, 70, 80];
const DESIGNATION_COLORS = {
  20: 'bg-emerald-500', 30: 'bg-green-500', 40: 'bg-lime-500',
  50: 'bg-orange-500', 60: 'bg-orange-500', 70: 'bg-red-400', 80: 'bg-red-600',
};

const REASONS = [
  'initial_designation', 'lease_up', 'redesignation_swap', 'over_income_correction',
  'vacancy_fill', 'compliance_adjustment', 'other'
];

const OCCUPANCY_OPTIONS = [
  { value: 'occupied', label: 'Occupied' },
  { value: 'leased', label: 'Leased' },
  { value: 'notice', label: 'Notice' },
  { value: 'vacant', label: 'Vacant' },
  { value: 'model', label: 'Model' },
  { value: 'offline', label: 'Offline' },
];

const PROGRAMS = [
  { key: 'tax_credit', label: 'Tax Credit' },
  { key: 'bonds', label: 'Bonds' },
  { key: 'home', label: 'HOME' },
  { key: 'hud', label: 'HUD' },
  { key: 'rd', label: 'RD' },
  { key: 'lhtf', label: 'LHTF' },
  { key: 'arpa', label: 'ARPA' },
  { key: 'psvs', label: 'PSVS' },
  { key: 'fwhg', label: 'FWHG' },
  { key: 'sail', label: 'SAIL' },
  { key: 'mmrb', label: 'MMRB' },
];

function AitBuildingDetail() {
  const { id: propertyId, buildingId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const {
    fetchProperty, fetchBuilding, fetchUnits, calculateBuilding, createUnit, updateUnit, deleteUnit,
    redesignateUnit, convertToMarketRate, convertToQualified, swapUnits, modelScenario,
    createSnapshot, evaluateOverIncome, fetchSnapshots, shareSnapshot, exportSnapshotPdf, lookupIncomeLimit, parseTicUpload,
    currentProperty, units, calculation, setCalculation
  } = useAit();

  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddUnit, setShowAddUnit] = useState(false);
  const [unitForm, setUnitForm] = useState({ unit_number: '', bedroom_count: 1, current_designation: '', qualified: true, initial_qualified: false, is_manager_unit: false });

  // Action modal state
  const [actionModal, setActionModal] = useState(null);
  // { type: 'redesignate' | 'to_market' | 'to_qualified' | 'swap', unit, ... }
  const [actionForm, setActionForm] = useState({ designation: 60, reason: 'redesignation_swap', notes: '', target_unit_id: '' });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const UNITS_PER_PAGE = 25;

  // Unit filters
  const [filterBedroom, setFilterBedroom] = useState('all');
  const [filterDesignation, setFilterDesignation] = useState('all');
  const [filterOccupancy, setFilterOccupancy] = useState('all');

  // Tabs
  const [activeTab, setActiveTab] = useState('units');
  // Chat
  const [showChat, setShowChat] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // Edit unit modal
  const [editUnitModal, setEditUnitModal] = useState(null); // unit object or null
  const [actionsDropdownId, setActionsDropdownId] = useState(null); // unit id for open actions dropdown
  const actionsDropdownRef = useRef(null);
  const [editUnitForm, setEditUnitForm] = useState({});

  // Income limit lookup
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [showHhSizePrompt, setShowHhSizePrompt] = useState(false);
  const [promptHhSize, setPromptHhSize] = useState('');

  // Snapshots
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [expandedSnapshot, setExpandedSnapshot] = useState(null);

  // TIC upload in edit modal
  const [ticUploadLoading, setTicUploadLoading] = useState(false);

  // Unit detail expand
  const [expandedUnitId, setExpandedUnitId] = useState(null);

  // Status change popover
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // { unit, newStatus }

  // Unit history modal
  const [unitHistoryModal, setUnitHistoryModal] = useState(null); // unit object or null

  // Over-income evaluation
  const [overIncomePanel, setOverIncomePanel] = useState(null); // { unitId, loading, data }

  // Simulation mode
  const [simActive, setSimActive] = useState(false);
  const [simChanges, setSimChanges] = useState(() => new Map());
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simApplying, setSimApplying] = useState(false);
  const [simApplyProgress, setSimApplyProgress] = useState(0);
  const [simOccupancyChanges, setSimOccupancyChanges] = useState(() => new Map());
  const [simConfirmModal, setSimConfirmModal] = useState(false);
  const [shareModal, setShareModal] = useState(null); // { url, expiresAt } or null
  const simDebounceRef = useRef(null);
  const simChangesRef = useRef(simChanges);
  simChangesRef.current = simChanges;
  const simOccupancyRef = useRef(simOccupancyChanges);
  simOccupancyRef.current = simOccupancyChanges;

  const isDark = theme === 'dark';

  // Close actions dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(e.target)) {
        setActionsDropdownId(null);
      }
    };
    if (actionsDropdownId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [actionsDropdownId]);

  useEffect(() => {
    loadData();
  }, [propertyId, buildingId]);

  const loadData = async () => {
    setLoading(true);
    if (!currentProperty || String(currentProperty.id) !== String(propertyId)) {
      await fetchProperty(propertyId);
    }
    const data = await fetchBuilding(propertyId, buildingId);
    if (data) setBuilding(data.building);
    await calculateBuilding(propertyId, buildingId);
    setLoading(false);
  };

  // Derived stats
  const stats = useMemo(() => {
    if (!calculation) return null;
    return calculation;
  }, [calculation]);

  // When simulation is active with results, overlay simulated stats on the dashboard
  const displayStats = simActive && simResult?.after ? simResult.after : stats;

  const qualifiedUnits = useMemo(() => units.filter(u => u.qualified), [units]);
  const marketRateUnits = useMemo(() => units.filter(u => !u.qualified), [units]);
  const undesignatedUnits = useMemo(() => units.filter(u => u.qualified && !u.current_designation), [units]);

  // Distribution for bar chart
  const distribution = useMemo(() => {
    const src = displayStats;
    if (!src?.designation_distribution) return [];
    return DESIGNATIONS.map(d => ({
      band: d,
      count: src.designation_distribution[String(d)] || 0,
    }));
  }, [displayStats]);

  const maxCount = Math.max(1, ...distribution.map(d => d.count));

  // Filtered units
  const filteredUnits = useMemo(() => {
    let result = units;
    if (filterBedroom !== 'all') {
      result = result.filter(u => String(u.bedroom_count) === filterBedroom);
    }
    if (filterDesignation !== 'all') {
      if (filterDesignation === 'none') {
        result = result.filter(u => !u.current_designation);
      } else if (filterDesignation === 'market') {
        result = result.filter(u => !u.qualified);
      } else {
        result = result.filter(u => String(u.current_designation) === filterDesignation);
      }
    }
    if (filterOccupancy !== 'all') {
      result = result.filter(u => u.occupancy_status === filterOccupancy);
    }
    return result;
  }, [units, filterBedroom, filterDesignation, filterOccupancy]);

  const hasActiveFilters = filterBedroom !== 'all' || filterDesignation !== 'all' || filterOccupancy !== 'all';

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [filterBedroom, filterDesignation, filterOccupancy]);

  // Pagination
  const totalPages = Math.ceil(filteredUnits.length / UNITS_PER_PAGE);
  const paginatedUnits = filteredUnits.slice((currentPage - 1) * UNITS_PER_PAGE, currentPage * UNITS_PER_PAGE);

  // Available bedroom options (derived from data)
  const bedroomOptions = useMemo(() => {
    const brs = [...new Set(units.map(u => u.bedroom_count).filter(b => b != null))].sort((a, b) => a - b);
    return brs;
  }, [units]);

  // Reset page when units change
  useEffect(() => {
    if (currentPage > Math.ceil(units.length / UNITS_PER_PAGE)) setCurrentPage(1);
  }, [units.length]);

  // Handlers

  const handleAddUnit = async (e) => {
    e.preventDefault();
    const data = { ...unitForm };
    if (!data.current_designation) delete data.current_designation;
    const result = await createUnit(buildingId, data);
    if (result.success) {
      toast.success(`Unit ${data.unit_number} added`);
      setShowAddUnit(false);
      setUnitForm({ unit_number: '', bedroom_count: 1, current_designation: '', qualified: true, initial_qualified: false, is_manager_unit: false });
      await calculateBuilding(propertyId, buildingId);
    } else {
      toast.error(Array.isArray(result.error) ? result.error.join(', ') : result.error);
    }
  };

  const handleOccupancyChange = (unit, newStatus) => {
    // For model/offline, save immediately (no dates needed)
    if (['model', 'offline'].includes(newStatus)) {
      handleOccupancyConfirm(unit, newStatus, {});
      return;
    }
    // For leased/notice/occupied/vacant, show popover for date entry
    setPendingStatusChange({ unit, newStatus });
  };

  const handleOccupancyConfirm = async (unit, newStatus, dates) => {
    setPendingStatusChange(null);
    const payload = { occupancy_status: newStatus, ...dates };
    const result = await updateUnit(buildingId, unit.id, payload);
    if (result.success) {
      toast.success(`Unit ${unit.unit_number} marked as ${newStatus}`);
      await calculateBuilding(propertyId, buildingId);
    } else {
      toast.error(Array.isArray(result.error) ? result.error.join(', ') : result.error || 'Failed to update');
    }
  };

  const handleDeleteUnit = async (unit) => {
    if (!confirm(`Delete unit ${unit.unit_number}?`)) return;
    const result = await deleteUnit(buildingId, unit.id);
    if (result.success) {
      toast.success('Unit deleted');
      await calculateBuilding(propertyId, buildingId);
    }
  };

  const handleAction = async () => {
    if (!actionModal) return;
    const { type, unit } = actionModal;
    let result;

    if (type === 'redesignate') {
      result = await redesignateUnit(buildingId, unit.id, actionForm.designation, actionForm.reason, actionForm.notes);
    } else if (type === 'to_market') {
      result = await convertToMarketRate(buildingId, unit.id, actionForm.reason, actionForm.notes);
    } else if (type === 'to_qualified') {
      result = await convertToQualified(buildingId, unit.id, actionForm.designation, actionForm.reason, actionForm.notes);
    } else if (type === 'swap') {
      result = await swapUnits(buildingId, unit.id, actionForm.target_unit_id, actionForm.designation, actionForm.reason, actionForm.notes);
    }

    if (result?.success) {
      toast.success(type === 'swap' ? 'Units swapped' : type === 'to_market' ? 'Converted to market rate' : type === 'to_qualified' ? 'Unit qualified' : 'Redesignated');
      setActionModal(null);
    } else {
      toast.error(Array.isArray(result?.error) ? result.error.join(', ') : result?.error || 'Action failed');
    }
  };

  const handleSnapshot = async () => {
    const opts = simActive ? { snapshot_type: 'simulation' } : {};
    const result = await createSnapshot(propertyId, buildingId, opts);
    if (result.success) toast.success(simActive ? 'Simulation snapshot saved' : 'Snapshot saved');
    else toast.error(result.error || 'Failed');
  };

  const handleEvaluateOverIncome = async (unit) => {
    setOverIncomePanel({ unitId: unit.id, loading: true, data: null });
    const result = await evaluateOverIncome(buildingId, unit.id);
    if (result.success) {
      setOverIncomePanel({ unitId: unit.id, loading: false, data: result });
    } else {
      toast.error(result.error || 'Unable to evaluate over-income');
      setOverIncomePanel(null);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    const result = await calculateBuilding(propertyId, buildingId);
    if (result) toast.success('Calculation updated');
    else toast.error('Calculation failed');
    setRecalculating(false);
  };

  // Simulation mode handlers
  const runSimScenario = useCallback(async () => {
    const desigChanges = Array.from(simChangesRef.current.entries()).map(([unit_id, new_designation]) => ({
      unit_id,
      new_designation,
    }));
    const occChanges = Array.from(simOccupancyRef.current.entries()).map(([unit_id, occupancy_status]) => ({
      unit_id,
      occupancy_status,
    }));
    const changes = [...desigChanges, ...occChanges];
    if (changes.length === 0) {
      setSimResult(null);
      return;
    }
    setSimLoading(true);
    const result = await modelScenario(propertyId, buildingId, changes);
    setSimResult(result);
    setSimLoading(false);
  }, [propertyId, buildingId, modelScenario]);

  const handleSimDesignationChange = useCallback((unitId, newDesignation) => {
    setSimChanges(prev => {
      const next = new Map(prev);
      const realUnit = units.find(u => u.id === unitId);
      if (realUnit && realUnit.current_designation === newDesignation) {
        next.delete(unitId);
      } else {
        next.set(unitId, newDesignation);
      }
      return next;
    });
    if (simDebounceRef.current) clearTimeout(simDebounceRef.current);
    simDebounceRef.current = setTimeout(() => runSimScenario(), 500);
  }, [units, runSimScenario]);

  const handleSimOccupancyChange = useCallback((unitId, newStatus) => {
    setSimOccupancyChanges(prev => {
      const next = new Map(prev);
      const realUnit = units.find(u => u.id === unitId);
      if (realUnit && (realUnit.occupancy_status || 'occupied') === newStatus) {
        next.delete(unitId);
      } else {
        next.set(unitId, newStatus);
      }
      return next;
    });
    if (simDebounceRef.current) clearTimeout(simDebounceRef.current);
    simDebounceRef.current = setTimeout(() => runSimScenario(), 500);
  }, [units, runSimScenario]);

  const handleSimResetUnit = useCallback((unitId) => {
    setSimChanges(prev => {
      const next = new Map(prev);
      next.delete(unitId);
      return next;
    });
    setSimOccupancyChanges(prev => {
      const next = new Map(prev);
      next.delete(unitId);
      return next;
    });
    if (simDebounceRef.current) clearTimeout(simDebounceRef.current);
    simDebounceRef.current = setTimeout(() => runSimScenario(), 500);
  }, [runSimScenario]);

  const handleSimDiscard = useCallback(() => {
    setSimActive(false);
    setSimChanges(new Map());
    setSimOccupancyChanges(new Map());
    setSimResult(null);
    setSimLoading(false);
    setSimApplying(false);
    setSimApplyProgress(0);
    setSimConfirmModal(false);
    if (simDebounceRef.current) clearTimeout(simDebounceRef.current);
  }, []);

  const handleSimApply = useCallback(async () => {
    const totalChanges = simChanges.size + simOccupancyChanges.size;
    if (totalChanges === 0) return;
    setSimConfirmModal(false);
    setSimApplying(true);
    setSimApplyProgress(0);
    let applied = 0;
    let failed = 0;
    // Apply designation changes
    for (const [unitId, newDesignation] of simChanges.entries()) {
      const result = await redesignateUnit(buildingId, unitId, newDesignation, 'compliance_adjustment', 'Applied from simulation mode');
      if (result?.success) {
        applied++;
      } else {
        failed++;
        const unit = units.find(u => u.id === unitId);
        toast.error(`Failed to redesignate unit ${unit?.unit_number || unitId}`);
      }
      setSimApplyProgress(applied + failed);
    }
    // Apply occupancy changes
    for (const [unitId, newStatus] of simOccupancyChanges.entries()) {
      const result = await updateUnit(buildingId, unitId, { occupancy_status: newStatus });
      if (result?.success !== false) {
        applied++;
      } else {
        failed++;
        const unit = units.find(u => u.id === unitId);
        toast.error(`Failed to update unit ${unit?.unit_number || unitId}`);
      }
      setSimApplyProgress(applied + failed);
    }
    if (applied > 0) {
      toast.success(`${applied} change(s) applied successfully`);
    }
    await calculateBuilding(propertyId, buildingId);
    handleSimDiscard();
  }, [simChanges, simOccupancyChanges, buildingId, redesignateUnit, updateUnit, units, calculateBuilding, propertyId, handleSimDiscard]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (simDebounceRef.current) clearTimeout(simDebounceRef.current);
    };
  }, []);

  // Edit unit
  const handleOpenEditUnit = (unit) => {
    setEditUnitForm({
      household_income: unit.household_income || '',
      income_limit: unit.income_limit || '',
      household_income_percent: unit.household_income_percent || '',
      household_size: unit.household_size || '',
      rent_amount: unit.rent_amount || '',
      lease_rent: unit.lease_rent || '',
      utility_allowance: unit.utility_allowance || '',
      tenant_id: unit.tenant_id || '',
      tenant_name: unit.tenant_name || '',
      move_in_date: unit.move_in_date || '',
      move_out_date: unit.move_out_date || '',
      lease_start_date: unit.lease_start_date || '',
      lease_end_date: unit.lease_end_date || '',
      recert_due_date: unit.recert_due_date || '',
      bedroom_count: unit.bedroom_count ?? 1,
      square_footage: unit.square_footage || '',
      occupancy_status: unit.occupancy_status || 'occupied',
      initial_qualified: unit.initial_qualified || false,
      is_manager_unit: unit.is_manager_unit || false,
      ada_accessible: unit.ada_accessible || false,
      hearing_accessible: unit.hearing_accessible || false,
      vision_accessible: unit.vision_accessible || false,
      program_flags: unit.program_flags || {},
      program_designations: unit.program_designations || {},
    });
    setEditUnitModal(unit);
    setLookupResult(null);
    setShowHhSizePrompt(false);
  };

  const handleSaveEditUnit = async (e) => {
    e.preventDefault();
    const data = { ...editUnitForm };
    // Convert numeric fields
    if (data.household_income !== '') data.household_income = Number(data.household_income);
    else delete data.household_income;
    if (data.income_limit !== '') data.income_limit = Number(data.income_limit);
    else delete data.income_limit;
    if (data.household_income_percent !== '') data.household_income_percent = Number(data.household_income_percent);
    else delete data.household_income_percent;
    if (data.household_size !== '') data.household_size = Number(data.household_size);
    else delete data.household_size;
    if (data.rent_amount !== '') data.rent_amount = Number(data.rent_amount);
    else delete data.rent_amount;
    if (data.utility_allowance !== '') data.utility_allowance = Number(data.utility_allowance);
    else delete data.utility_allowance;
    if (data.square_footage !== '') data.square_footage = Number(data.square_footage);
    else delete data.square_footage;
    if (data.lease_rent !== '') data.lease_rent = Number(data.lease_rent);
    else delete data.lease_rent;
    if (!data.move_in_date) delete data.move_in_date;
    if (!data.move_out_date) delete data.move_out_date;
    if (!data.lease_start_date) delete data.lease_start_date;
    if (!data.lease_end_date) delete data.lease_end_date;
    if (!data.recert_due_date) delete data.recert_due_date;
    if (!data.tenant_id) delete data.tenant_id;
    if (!data.tenant_name) delete data.tenant_name;

    const result = await updateUnit(buildingId, editUnitModal.id, data);
    if (result.success) {
      toast.success(`Unit ${editUnitModal.unit_number} updated`);
      setEditUnitModal(null);
      await calculateBuilding(propertyId, buildingId);
    } else {
      toast.error(Array.isArray(result.error) ? result.error.join(', ') : result.error || 'Failed to update');
    }
  };

  // Income limit AI lookup — hits model service directly (no Rails proxy)
  const runIncomeLimitLookup = async (hhSize) => {
    if (!currentProperty) {
      toast.error('Property data not loaded yet');
      return;
    }
    const designation = editUnitModal.current_designation;
    setLookupLoading(true);
    setLookupResult(null);
    setShowHhSizePrompt(false);
    const result = await lookupIncomeLimit({
      address: [currentProperty.address, currentProperty.city, currentProperty.state, currentProperty.zip_code].filter(Boolean).join(', '),
      state: currentProperty.state || '',
      county: currentProperty.county || '',
      zip_code: currentProperty.zip_code || '',
      designation,
      household_size: Number(hhSize),
      bedroom_count: editUnitForm.bedroom_count || 1,
    });
    setLookupLoading(false);
    if (result.success && result.income_limit) {
      setLookupResult(result);
      setEditUnitForm(f => {
        const updated = { ...f, income_limit: result.income_limit, household_size: hhSize };
        if (f.household_income) {
          updated.household_income_percent = (Number(f.household_income) / Number(result.income_limit) * 100).toFixed(2);
        }
        return updated;
      });
      toast.success(`Income limit found: $${Number(result.income_limit).toLocaleString()}`);
    } else {
      setLookupResult({ error: result.error || 'Could not find income limits' });
      toast.error(result.error || 'Could not find income limits for this area');
    }
  };

  const handleLookupIncomeLimit = () => {
    if (!editUnitModal) return;
    if (!editUnitModal.current_designation) {
      toast.error('Unit must have a designation to look up income limits');
      return;
    }
    const hhSize = editUnitForm.household_size;
    if (!hhSize || Number(hhSize) < 1) {
      // Show inline prompt for household size instead of just a toast
      setShowHhSizePrompt(true);
      setPromptHhSize('');
      return;
    }
    runIncomeLimitLookup(hhSize);
  };

  // TIC upload in edit modal
  const handleTicUploadInModal = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    setTicUploadLoading(true);
    const result = await parseTicUpload(file);
    setTicUploadLoading(false);
    if (result.success && result.unit_data) {
      const d = result.unit_data;
      setEditUnitForm(f => {
        const updated = { ...f };
        if (d.household_income) updated.household_income = d.household_income;
        if (d.income_limit) updated.income_limit = d.income_limit;
        if (d.household_income_percent) updated.household_income_percent = d.household_income_percent;
        else if (d.household_income && d.income_limit) {
          updated.household_income_percent = (d.household_income / d.income_limit * 100).toFixed(2);
        }
        if (d.household_size) updated.household_size = d.household_size;
        if (d.rent_amount) updated.rent_amount = d.rent_amount;
        if (d.utility_allowance) updated.utility_allowance = d.utility_allowance;
        if (d.move_in_date) updated.move_in_date = d.move_in_date;
        if (d.bedroom_count) updated.bedroom_count = d.bedroom_count;
        if (d.occupancy_status) updated.occupancy_status = d.occupancy_status;
        return updated;
      });
      if (result.unit_data.income_limit) {
        setLookupResult({
          income_limit: result.unit_data.income_limit,
          max_rent: result.unit_data.max_rent_amount || null,
          area_name: result.unit_data.county || '',
          source_url: null,
          notes: `Extracted from TIC: ${file.name}`,
        });
      }
      toast.success('TIC parsed — review fields and save');
    } else {
      toast.error(result.error || 'Failed to parse TIC');
    }
  };

  // Snapshots
  const loadSnapshots = async () => {
    setSnapshotsLoading(true);
    const result = await fetchSnapshots(buildingId);
    if (result.success) setSnapshots(result.snapshots);
    setSnapshotsLoading(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={32} /></div>;
  }

  if (!building) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10 text-center">
        <p>Building not found.</p>
        <button onClick={() => navigate(`/dashboard/ait/${propertyId}`)} className="text-blue-500 mt-2">Back to property</button>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Back */}
      <button onClick={() => navigate(`/dashboard/ait/${propertyId}`)}
        className={`flex items-center gap-1 text-sm mb-4 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
        <ArrowLeft size={16} /> Back to property
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-dm-serif text-gray-900 dark:text-white">{building.name || building.bin_number}</h1>
          {building.bin_number && building.name && (
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>BIN: {building.bin_number}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowChat(true)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
            <Sparkles size={12} /> JPRO Assistant
          </button>
          <button onClick={handleRecalculate} disabled={recalculating}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50">
            <RefreshCw size={14} className={recalculating ? 'animate-spin' : ''} /> Recalculate
          </button>
          <button onClick={handleSnapshot} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
            <Camera size={14} /> Save Snapshot
          </button>
          <button
            onClick={() => {
              if (simActive) {
                handleSimDiscard();
              } else {
                setSimActive(true);
                setSimChanges(new Map());
                setSimResult(null);
              }
            }}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
              simActive
                ? 'border-indigo-400 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-400'
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <FlaskConical size={14} /> {simActive ? 'Exit Simulation' : 'Simulate'}
          </button>
        </div>
      </div>

      {/* Simulation Mode Banner */}
      {simActive && (
        <div className={`flex items-center justify-between gap-3 p-3 mb-4 rounded-lg border ${
          isDark ? 'bg-indigo-900/20 border-indigo-500/30' : 'bg-indigo-50 border-indigo-200'
        }`}>
          <div className="flex items-center gap-2">
            <FlaskConical size={16} className="text-indigo-500 shrink-0" />
            <span className={`text-sm font-medium ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
              Simulation Mode
            </span>
            <span className={`text-xs ${isDark ? 'text-indigo-400/70' : 'text-indigo-600'}`}>
              {(simChanges.size + simOccupancyChanges.size) === 0 ? 'Change designations or occupancy status below to preview impact' : `${simChanges.size + simOccupancyChanges.size} change${(simChanges.size + simOccupancyChanges.size) !== 1 ? 's' : ''} pending`}
            </span>
            {simLoading && <Loader2 size={14} className="animate-spin text-indigo-500" />}
          </div>
          <div className="flex items-center gap-2">
            {simApplying ? (
              <span className={`text-xs font-medium ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
                <Loader2 size={12} className="inline animate-spin mr-1" />
                Applying ({simApplyProgress}/{simChanges.size})...
              </span>
            ) : (
              <>
                <button onClick={handleSimDiscard}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                    isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}>
                  Discard
                </button>
                <button onClick={() => setSimConfirmModal(true)}
                  disabled={(simChanges.size + simOccupancyChanges.size) === 0}
                  className="text-xs px-3 py-1.5 rounded-lg bg-indigo-800 dark:bg-orange-600 text-white dark:text-white hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Apply Changes
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Simulation Summary Panel */}
      {simActive && simResult && (simChanges.size + simOccupancyChanges.size) > 0 && (
        <div className={`p-4 rounded-xl border mb-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div className="text-center">
              <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Before</div>
              <div className={`text-xl font-bold ${simResult.before?.average_income <= 60 ? 'text-green-500' : 'text-red-500'}`}>
                {simResult.before?.average_income ?? '—'}%
              </div>
            </div>
            <div className="text-center">
              <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>After</div>
              <div className={`text-xl font-bold ${simResult.after?.average_income <= 60 ? 'text-green-500' : 'text-red-500'}`}>
                {simResult.after?.average_income ?? '—'}%
              </div>
            </div>
            <div className="text-center">
              <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Delta</div>
              <div className={`text-xl font-bold ${
                simResult.delta < 0 ? 'text-green-500' :
                simResult.delta > 0 ? 'text-red-500' :
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {simResult.delta != null ? `${simResult.delta > 0 ? '+' : ''}${simResult.delta}%` : '—'}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Array.from(simChanges.entries()).map(([unitId, newDes]) => {
              const unit = units.find(u => u.id === unitId);
              return (
                <span key={`des-${unitId}`} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                  isDark ? 'bg-indigo-900/30 text-indigo-300 border border-indigo-500/30' : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                }`}>
                  #{unit?.unit_number}: {unit?.current_designation}% → {newDes}%
                  <button onClick={() => handleSimResetUnit(unitId)} className="ml-0.5 hover:text-red-500">
                    <XCircle size={12} />
                  </button>
                </span>
              );
            })}
            {Array.from(simOccupancyChanges.entries()).map(([unitId, newStatus]) => {
              const unit = units.find(u => u.id === unitId);
              return (
                <span key={`occ-${unitId}`} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                  isDark ? 'bg-orange-900/30 text-orange-300 border border-orange-500/30' : 'bg-orange-50 text-orange-700 border border-orange-200'
                }`}>
                  #{unit?.unit_number}: {unit?.occupancy_status || 'occupied'} → {newStatus}
                  <button onClick={() => handleSimResetUnit(unitId)} className="ml-0.5 hover:text-red-500">
                    <XCircle size={12} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Compliance Dashboard */}
      {stats && stats.average_income !== null && (
        <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6`}>
          <div className={`p-4 rounded-xl border ${simActive && simResult?.after ? (isDark ? 'bg-gray-800 border-indigo-500/40 ring-1 ring-indigo-500/20' : 'bg-white border-indigo-300 ring-1 ring-indigo-200') : (isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200')}`}>
            <div className={`text-2xl font-bold ${displayStats.compliant !== undefined ? (displayStats.compliant ? 'text-green-500' : 'text-red-500') : (displayStats.average_income <= 60 ? 'text-green-500' : 'text-red-500')}`}>
              {displayStats.average_income}%
            </div>
            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Average Income</div>
            {displayStats.headroom != null && (
              <div className={`text-xs mt-1 ${displayStats.headroom >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {displayStats.headroom >= 0 ? `${displayStats.headroom}% headroom` : `${Math.abs(displayStats.headroom)}% over limit`}
              </div>
            )}
            {simActive && simResult?.delta != null && (
              <div className={`text-xs mt-1 font-medium ${simResult.delta < 0 ? 'text-green-500' : simResult.delta > 0 ? 'text-red-500' : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
                {simResult.delta > 0 ? '+' : ''}{simResult.delta}% from simulation
              </div>
            )}
          </div>
          <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="text-2xl font-bold">{displayStats.designated_count || displayStats.qualified_count}</div>
            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Qualified of {displayStats.total_units} ({displayStats.qualified_percentage}%)
            </div>
            {displayStats.market_rate_count > 0 && (
              <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {displayStats.market_rate_count} market rate
              </div>
            )}
            {displayStats.manager_unit_count > 0 && (
              <div className={`text-xs mt-0.5 text-purple-500 dark:text-purple-400`}>
                {displayStats.manager_unit_count} manager (excluded)
              </div>
            )}
            {(displayStats.excluded_units?.offline > 0 || displayStats.excluded_units?.model > 0) && (
              <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {[
                  displayStats.excluded_units?.offline > 0 && `${displayStats.excluded_units.offline} offline`,
                  displayStats.excluded_units?.model > 0 && `${displayStats.excluded_units.model} model`,
                ].filter(Boolean).join(', ')} (excluded)
              </div>
            )}
          </div>
          <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`text-sm font-medium capitalize ${
              displayStats.flexibility_index === 'high' ? 'text-green-500' :
              displayStats.flexibility_index === 'moderate' ? 'text-blue-500' :
              displayStats.flexibility_index === 'constrained' ? 'text-orange-500' :
              'text-red-500'
            }`}>{displayStats.flexibility_index}</div>
            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Flexibility</div>
            <div className={`text-xs mt-1.5 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {displayStats.flexibility_index === 'high'
                ? 'Wide room to redesignate units without risking compliance'
                : displayStats.flexibility_index === 'moderate'
                ? 'Some room for changes, but watch the average closely'
                : displayStats.flexibility_index === 'constrained'
                ? 'Limited options — most changes could push average over 60%'
                : 'No safe moves available without exceeding the income limit'}
            </div>
          </div>
          <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`text-sm font-medium capitalize ${
              displayStats.pressure_level === 'low' ? 'text-green-500' :
              displayStats.pressure_level === 'moderate' ? 'text-orange-500' :
              displayStats.pressure_level === 'elevated' ? 'text-orange-500' :
              'text-red-500'
            }`}>{displayStats.pressure_level}</div>
            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Pressure Level</div>
            <div className={`text-xs mt-1.5 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {displayStats.pressure_level === 'low'
                ? 'Comfortable margin — average is well below the 60% threshold'
                : displayStats.pressure_level === 'moderate'
                ? 'Getting closer to the limit — plan redesignations carefully'
                : displayStats.pressure_level === 'elevated'
                ? 'Near the threshold — one bad move could trigger non-compliance'
                : 'At or above 60% — immediate action needed to restore compliance'}
            </div>
          </div>
        </div>
      )}

      {/* Below 40% minimum qualified threshold banner */}
      {stats?.minimum_qualified_met === false && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 text-sm font-medium">
          <XCircle size={16} className="shrink-0" />
          <div>
            <span className="font-semibold">Below Minimum Qualified Threshold.</span>{' '}
            Only {stats.qualified_percentage}% of units are qualified — at least 40% is required for compliance.
          </div>
        </div>
      )}

      {/* Lease-up: below 40% but not enforced yet */}
      {stats?.in_lease_up && stats?.qualified_percentage < 40 && stats?.minimum_qualified_met !== false && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-400 text-sm">
          <AlertTriangle size={16} className="shrink-0" />
          <div>
            <span className="font-semibold">Lease-Up in Progress.</span>{' '}
            Currently {stats.qualified_percentage}% qualified — 40% minimum required by end of first credit year.
          </div>
        </div>
      )}

      {/* Over-income units banner */}
      {units.some(u => u.over_income_flag) && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
          <AlertOctagon size={16} className="shrink-0" />
          <div>
            <span className="font-semibold">{units.filter(u => u.over_income_flag).length} over-income unit(s) detected.</span>{' '}
            Click the shield icon on a unit row to evaluate correction pathways.
          </div>
        </div>
      )}

      {/* Needs designation banner */}
      {stats?.needs_designation && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-sm">
          <AlertTriangle size={16} />
          {undesignatedUnits.length} unit(s) need a designation. Click a unit below to assign one.
        </div>
      )}

      {/* Concentration risks */}
      {stats?.concentration_risks?.length > 0 && (
        <div className="space-y-1 mb-4">
          {stats.concentration_risks.map((risk, i) => (
            <div key={i} className={`flex items-center gap-2 text-xs p-2 rounded-lg ${
              risk.severity === 'critical' ? 'bg-red-10 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
              risk.severity === 'warning' ? 'bg-orange-10 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400' :
              risk.severity === 'alert' ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' :
              'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
            }`}>
              <AlertTriangle size={12} /> {risk.detail}
            </div>
          ))}
        </div>
      )}

      {/* Designation distribution */}
      {distribution.some(d => d.count > 0) && (
        <div className={`p-4 rounded-xl border mb-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">Designation Distribution</h3>
          <div className="flex items-end gap-2 h-24">
            {distribution.map(d => (
              <div key={d.band} className="flex-1 flex flex-col items-center gap-1">
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{d.count}</span>
                <div className={`w-full rounded-t ${DESIGNATION_COLORS[d.band]}`}
                  style={{ height: `${(d.count / maxCount) * 80}px`, minHeight: d.count > 0 ? '4px' : '0' }} />
                <span className="text-xs font-medium">{d.band}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className={`flex gap-1 mb-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <button
          onClick={() => setActiveTab('units')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'units'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}>
          Units
        </button>
        <button
          onClick={() => setActiveTab('monthly')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'monthly'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}>
          <Calendar size={14} /> Monthly Tracking
        </button>
        <button
          onClick={() => { setActiveTab('snapshots'); loadSnapshots(); }}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'snapshots'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}>
          <Camera size={14} /> Snapshots
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'history'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}>
          <Clock size={14} /> History
        </button>
      </div>

      {/* Monthly Tracking Grid */}
      {activeTab === 'monthly' && (
        <AitMonthlyGrid buildingId={buildingId} propertyId={propertyId} />
      )}

      {/* Snapshots Tab */}
      {activeTab === 'snapshots' && (
        <div className={`rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
          <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">Snapshot History</h2>
            <button onClick={async () => { const opts = simActive ? { snapshot_type: 'simulation' } : {}; const r = await createSnapshot(propertyId, buildingId, opts); if (r.success) { toast.success(simActive ? 'Simulation snapshot saved' : 'Snapshot saved'); loadSnapshots(); } else toast.error(r.error || 'Failed'); }}
              className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600">
              <Camera size={14} /> Save New Snapshot
            </button>
          </div>
          {snapshotsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-blue-500" size={24} />
            </div>
          ) : snapshots.length === 0 ? (
            <div className={`text-center py-12 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              No snapshots yet. Save a snapshot to capture the current compliance state.
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {snapshots.map(snap => {
                const isExpanded = expandedSnapshot === snap.id;
                return (
                  <div key={snap.id}>
                    <button
                      onClick={() => setExpandedSnapshot(isExpanded ? null : snap.id)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${snap.compliance_position === 'compliant' ? 'bg-green-500' : snap.compliance_position === 'non_compliant' ? 'bg-red-500' : 'bg-orange-500'}`} />
                        <div>
                          <div className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {new Date(snap.created_at).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            {snap.snapshot_type === 'simulation' ? (
                              <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                isDark ? 'bg-indigo-900/40 text-indigo-300 border border-indigo-500/30' : 'bg-indigo-50 text-indigo-600 border border-indigo-200'
                              }`}>
                                <FlaskConical size={10} /> Simulation
                              </span>
                            ) : (
                              <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                isDark ? 'bg-green-900/40 text-green-300 border border-green-500/30' : 'bg-green-50 text-green-600 border border-green-200'
                              }`}>
                                <Camera size={10} /> Live
                              </span>
                            )}
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Avg: {snap.average_income}% | {snap.qualified_count} qualified | {snap.compliance_position?.replace(/_/g, ' ')}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span
                          role="button"
                          title="Share link"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const r = await shareSnapshot(buildingId, snap.id);
                            if (r.success) {
                              setShareModal({ url: r.share_url, expiresAt: r.expires_at });
                            } else {
                              toast.error(r.error || 'Failed to generate share link');
                            }
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-500 hover:text-gray-300' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
                        >
                          <Share2 size={13} />
                        </span>
                        <span
                          role="button"
                          title="Download PDF"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const r = await exportSnapshotPdf(buildingId, snap.id);
                            if (r.success) {
                              const url = URL.createObjectURL(r.blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `snapshot-${snap.snapshot_date}.pdf`;
                              a.click();
                              URL.revokeObjectURL(url);
                              toast.success('PDF downloaded');
                            } else {
                              toast.error('Failed to export PDF');
                            }
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-500 hover:text-gray-300' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
                        >
                          <Download size={13} />
                        </span>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className={`px-4 pb-4 ${isDark ? 'bg-gray-800/30' : 'bg-gray-50/50'}`}>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-white'}`}>
                            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Average Income</div>
                            <div className={`text-lg font-bold ${snap.average_income <= 60 ? 'text-green-500' : 'text-red-500'}`}>{snap.average_income}%</div>
                          </div>
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-white'}`}>
                            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Qualified</div>
                            <div className="text-lg font-bold">{snap.qualified_count}<span className={`text-xs font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}> ({snap.qualified_percentage}%)</span></div>
                          </div>
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-white'}`}>
                            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Flexibility</div>
                            <div className={`text-sm font-medium capitalize ${
                              snap.flexibility_index === 'high' ? 'text-green-500' :
                              snap.flexibility_index === 'moderate' ? 'text-blue-500' :
                              snap.flexibility_index === 'constrained' ? 'text-orange-500' : 'text-red-500'
                            }`}>{snap.flexibility_index}</div>
                          </div>
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-white'}`}>
                            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Pressure</div>
                            <div className={`text-sm font-medium capitalize ${
                              snap.pressure_level === 'low' ? 'text-green-500' :
                              snap.pressure_level === 'moderate' ? 'text-orange-500' :
                              snap.pressure_level === 'elevated' ? 'text-orange-500' : 'text-red-500'
                            }`}>{snap.pressure_level}</div>
                          </div>
                        </div>
                        {/* Designation distribution mini-bars */}
                        {snap.designation_distribution && Object.keys(snap.designation_distribution).length > 0 && (
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-white'}`}>
                            <div className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Distribution</div>
                            <div className="flex items-end gap-1.5 h-12">
                              {DESIGNATIONS.map(d => {
                                const count = snap.designation_distribution[String(d)] || 0;
                                const maxSnap = Math.max(1, ...Object.values(snap.designation_distribution).map(Number));
                                return (
                                  <div key={d} className="flex-1 flex flex-col items-center gap-0.5">
                                    <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{count}</span>
                                    <div className={`w-full rounded-t ${DESIGNATION_COLORS[d]}`}
                                      style={{ height: `${(count / maxSnap) * 32}px`, minHeight: count > 0 ? '2px' : '0' }} />
                                    <span className="text-[10px] font-medium">{d}%</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* Concentration risks */}
                        {snap.concentration_risks?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {snap.concentration_risks.map((risk, i) => (
                              <div key={i} className={`flex items-center gap-2 text-xs p-1.5 rounded ${
                                risk.severity === 'critical' ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400' :
                                risk.severity === 'warning' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400' :
                                'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                              }`}>
                                <AlertTriangle size={10} /> {risk.detail}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Unit Table */}
      {/* History Tab */}
      {activeTab === 'history' && (
        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
          <AitHistoryTimeline scope="building" ids={{ propertyId, buildingId }} showUnit />
        </div>
      )}

      {activeTab === 'units' && <div className={`rounded-xl border overflow-hidden flex flex-col ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
        <div className={`flex items-center justify-between px-4 py-2.5 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className="text-sm font-medium text-gray-900 dark:text-white">
            Units <span className={`text-xs font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({hasActiveFilters ? `${filteredUnits.length} of ${units.length}` : units.length})</span>
          </h2>
          <button onClick={() => setShowAddUnit(true)}
            className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600">
            <Plus size={14} /> Add Unit
          </button>
        </div>

        {/* Filters */}
        <div className={`flex items-center gap-3 px-4 py-2 border-b ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-100 bg-gray-50/50'}`}>
          <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Filter:</span>
          <select
            value={filterBedroom}
            onChange={e => setFilterBedroom(e.target.value)}
            className={`text-xs rounded px-2 py-1 border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`}
          >
            <option value="all">All Bedrooms</option>
            {bedroomOptions.map(br => (
              <option key={br} value={String(br)}>{br === 0 ? 'Studio' : `${br} BR`}</option>
            ))}
          </select>
          <select
            value={filterDesignation}
            onChange={e => setFilterDesignation(e.target.value)}
            className={`text-xs rounded px-2 py-1 border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`}
          >
            <option value="all">All Designations</option>
            {DESIGNATIONS.map(d => (
              <option key={d} value={String(d)}>{d}% AMI</option>
            ))}
            <option value="none">Undesignated</option>
            <option value="market">Market Rate</option>
          </select>
          <select
            value={filterOccupancy}
            onChange={e => setFilterOccupancy(e.target.value)}
            className={`text-xs rounded px-2 py-1 border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`}
          >
            <option value="all">All Occupancy</option>
            {OCCUPANCY_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {hasActiveFilters && (
            <button
              onClick={() => { setFilterBedroom('all'); setFilterDesignation('all'); setFilterOccupancy('all'); }}
              className={`text-xs px-2 py-1 rounded ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
            >
              Clear
            </button>
          )}
        </div>

        <div className="overflow-auto max-h-[calc(100vh-380px)]">
          <table className={`w-full text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
            <thead className={`sticky top-0 z-10 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Unit</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">BR</th>
                <th className="px-2 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 w-16">Flags</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">IQG</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Designation</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">HH Income</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Occupancy</th>
                <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedUnits.map(unit => (
                <React.Fragment key={unit.id}>
                <tr
                  onClick={() => setExpandedUnitId(expandedUnitId === unit.id ? null : unit.id)}
                  className={`cursor-pointer ${isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'} transition-colors ${expandedUnitId === unit.id ? (isDark ? 'bg-gray-800/30' : 'bg-blue-50/50') : ''} ${simActive && simChanges.has(unit.id) ? (isDark ? 'border-l-2 border-l-indigo-500 bg-indigo-900/10' : 'border-l-2 border-l-indigo-400 bg-indigo-50/50') : ''} ${['offline', 'model'].includes(simActive && simOccupancyChanges.has(unit.id) ? simOccupancyChanges.get(unit.id) : unit.occupancy_status) ? 'opacity-50' : ''}`}
                >
                  <td className="px-4 py-2 font-medium">
                    <div className="flex items-center gap-1.5">
                      <ChevronDown size={12} className={`transition-transform ${isDark ? 'text-gray-500' : 'text-gray-400'} ${expandedUnitId === unit.id ? 'rotate-180' : ''}`} />
                      {unit.unit_number}
                    </div>
                  </td>
                  <td className="px-4 py-2">{unit.bedroom_count}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-center gap-0.5 flex-wrap">
                      {unit.is_manager_unit && (
                        <span className="inline-flex text-[10px] font-bold px-1 py-0 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400" title="Manager Unit">Mgr</span>
                      )}
                      {unit.ada_accessible && (
                        <Accessibility size={13} className="text-blue-500 dark:text-blue-400" title="ADA Accessible" />
                      )}
                      {unit.hearing_accessible && (
                        <Ear size={13} className="text-blue-500 dark:text-blue-400" title="Hearing Accessible" />
                      )}
                      {unit.vision_accessible && (
                        <Eye size={13} className="text-blue-500 dark:text-blue-400" title="Vision Accessible" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {unit.initial_qualified ? (
                      <CheckCircle2 size={14} className="text-green-500 inline" />
                    ) : (
                      <span className="text-xs opacity-40">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {unit.qualified ? (
                      unit.designated ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          <CheckCircle2 size={10} /> Qualified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                          Needs Designation
                        </span>
                      )
                    ) : (
                      <span className="inline-flex text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                        Market Rate
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2" onClick={simActive && unit.qualified && !['offline', 'model'].includes(simOccupancyChanges.has(unit.id) ? simOccupancyChanges.get(unit.id) : unit.occupancy_status) ? (e) => e.stopPropagation() : undefined}>
                    {simActive && unit.qualified && !['offline', 'model'].includes(simOccupancyChanges.has(unit.id) ? simOccupancyChanges.get(unit.id) : unit.occupancy_status) ? (
                      <select
                        value={simChanges.has(unit.id) ? simChanges.get(unit.id) : (unit.current_designation || '')}
                        onChange={(e) => handleSimDesignationChange(unit.id, parseInt(e.target.value))}
                        className={`text-xs font-medium rounded px-1.5 py-0.5 border cursor-pointer transition-colors ${
                          simChanges.has(unit.id)
                            ? 'ring-2 ring-indigo-400 border-indigo-400 dark:ring-indigo-500 dark:border-indigo-500'
                            : isDark ? 'border-gray-600' : 'border-gray-200'
                        } ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700'}`}
                      >
                        {DESIGNATIONS.map(d => (
                          <option key={d} value={d}>{d}%</option>
                        ))}
                      </select>
                    ) : unit.current_designation ? (
                      <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full text-white ${DESIGNATION_COLORS[unit.current_designation] || 'bg-gray-500'}`}>
                        {unit.current_designation}%
                      </span>
                    ) : (
                      <span className="text-xs opacity-40">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {unit.household_income_percent ? (
                      <span className={`inline-flex items-center gap-1 text-xs ${
                        unit.over_income_flag
                          ? 'text-red-600 dark:text-red-400 font-semibold'
                          : isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {unit.over_income_flag && <AlertOctagon size={12} />}
                        {unit.household_income_percent}%
                      </span>
                    ) : (
                      <span className="text-xs opacity-40">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const effectiveStatus = simActive && simOccupancyChanges.has(unit.id)
                        ? simOccupancyChanges.get(unit.id)
                        : (unit.occupancy_status || 'occupied');
                      return (
                        <div className="relative">
                          <select
                            value={effectiveStatus}
                            onChange={(e) => simActive
                              ? handleSimOccupancyChange(unit.id, e.target.value)
                              : handleOccupancyChange(unit, e.target.value)
                            }
                            className={`text-xs rounded px-1.5 py-0.5 border cursor-pointer ${
                              isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-200 text-gray-700'
                            } ${
                              effectiveStatus === 'vacant' ? '!text-red-500 dark:!text-red-400' :
                              effectiveStatus === 'leased' ? '!text-blue-500 dark:!text-blue-400' :
                              effectiveStatus === 'notice' ? '!text-orange-500 dark:!text-orange-400' :
                              effectiveStatus === 'offline' ? '!text-orange-500 dark:!text-orange-400' :
                              effectiveStatus === 'model' ? '!text-orange-500 dark:!text-orange-400' : ''
                            } ${
                              simActive && simOccupancyChanges.has(unit.id) ? 'ring-2 ring-indigo-400 dark:ring-indigo-500' : ''
                            }`}
                          >
                            {OCCUPANCY_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          {pendingStatusChange?.unit?.id === unit.id && (
                            <StatusChangePopover
                              unit={unit}
                              newStatus={pendingStatusChange.newStatus}
                              onConfirm={(status, dates) => handleOccupancyConfirm(unit, status, dates)}
                              onCancel={() => setPendingStatusChange(null)}
                            />
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    {simActive ? (
                      <div className="flex items-center justify-end gap-1">
                        {(simChanges.has(unit.id) || simOccupancyChanges.has(unit.id)) && (
                          <button onClick={() => handleSimResetUnit(unit.id)}
                            title="Reset to original"
                            className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                              isDark ? 'text-indigo-400 hover:bg-indigo-900/30' : 'text-indigo-600 hover:bg-indigo-50'
                            }`}>
                            <RotateCcw size={12} /> Reset
                          </button>
                        )}
                        {!unit.qualified && (
                          <button onClick={() => { setActionModal({ type: 'to_qualified', unit }); }}
                            title="Bring into program"
                            className="text-xs text-green-500 hover:underline">
                            Qualify
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="relative flex items-center justify-end" ref={actionsDropdownId === unit.id ? actionsDropdownRef : undefined}>
                        <button
                          onClick={() => setActionsDropdownId(actionsDropdownId === unit.id ? null : unit.id)}
                          className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                            actionsDropdownId === unit.id
                              ? (isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                          }`}
                        >
                          <span className="text-xs">Actions</span>
                          <MoreVertical size={14} />
                        </button>
                        {actionsDropdownId === unit.id && (
                          <div className={`absolute right-0 top-full mt-1 z-50 min-w-[180px] rounded-lg shadow-lg border py-1 ${
                            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                          }`}>
                            <button onClick={() => { handleOpenEditUnit(unit); setActionsDropdownId(null); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                                isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'
                              }`}>
                              <Pencil size={14} className="text-gray-400" /> Edit Unit
                            </button>
                            {unit.over_income_flag && (
                              <button onClick={() => { handleEvaluateOverIncome(unit); setActionsDropdownId(null); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                                  isDark ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-red-50 text-red-600'
                                }`}>
                                <ShieldAlert size={14} /> Evaluate Over-Income
                              </button>
                            )}
                            {unit.qualified && unit.designated && (
                              <>
                                <button onClick={() => { setActionModal({ type: 'redesignate', unit }); setActionForm(f => ({ ...f, designation: unit.current_designation })); setActionsDropdownId(null); }}
                                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                                    isDark ? 'hover:bg-gray-700 text-blue-400' : 'hover:bg-blue-50 text-blue-600'
                                  }`}>
                                  <TrendingUp size={14} /> Redesignate
                                </button>
                                <button onClick={() => { setActionModal({ type: 'to_market', unit }); setActionsDropdownId(null); }}
                                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                                    isDark ? 'hover:bg-gray-700 text-orange-400' : 'hover:bg-orange-50 text-orange-600'
                                  }`}>
                                  <TrendingDown size={14} /> Convert to Market
                                </button>
                                <button onClick={() => { setActionModal({ type: 'swap', unit }); setActionForm(f => ({ ...f, designation: unit.current_designation })); setActionsDropdownId(null); }}
                                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                                    isDark ? 'hover:bg-gray-700 text-purple-400' : 'hover:bg-purple-50 text-purple-600'
                                  }`}>
                                  <ArrowLeftRight size={14} /> Swap Unit
                                </button>
                              </>
                            )}
                            {unit.qualified && !unit.designated && (
                              <button onClick={() => { setActionModal({ type: 'to_qualified', unit }); setActionsDropdownId(null); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                                  isDark ? 'hover:bg-gray-700 text-blue-400' : 'hover:bg-blue-50 text-blue-600'
                                }`}>
                                <Plus size={14} /> Assign Designation
                              </button>
                            )}
                            {!unit.qualified && (
                              <button onClick={() => { setActionModal({ type: 'to_qualified', unit }); setActionsDropdownId(null); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                                  isDark ? 'hover:bg-gray-700 text-green-400' : 'hover:bg-green-50 text-green-600'
                                }`}>
                                <Plus size={14} /> Qualify Unit
                              </button>
                            )}
                            <div className={`my-1 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`} />
                            <button onClick={() => { handleDeleteUnit(unit); setActionsDropdownId(null); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                                isDark ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-600'
                              }`}>
                              <Trash2 size={14} /> Delete Unit
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                {/* Expanded unit detail */}
                {expandedUnitId === unit.id && (
                  <tr>
                    <td colSpan={9} className="px-0 py-0">
                      <div className={`px-6 py-4 ${isDark ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-xs">
                          {/* Income & Compliance */}
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Household Income</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.household_income ? `$${Number(unit.household_income).toLocaleString()}` : '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Income Limit</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.income_limit ? `$${Number(unit.income_limit).toLocaleString()}` : '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>HH Income % AMI</span>
                            <span className={`${unit.over_income_flag ? 'text-red-500 font-semibold' : isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                              {unit.household_income_percent ? `${unit.household_income_percent}%` : '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Over-Income</span>
                            <span className={unit.over_income_flag ? 'text-red-500 font-semibold' : isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.over_income_flag ? 'Yes' : 'No'}
                            </span>
                          </div>

                          {/* Tenant */}
                          <div className="col-span-2">
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Household Name</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.tenant_name || '—'}
                            </span>
                          </div>

                          {/* Rent & Housing */}
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Rent Amount</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.rent_amount ? `$${Number(unit.rent_amount).toLocaleString()}` : '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Lease Rent</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.lease_rent ? `$${Number(unit.lease_rent).toLocaleString()}` : '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Utility Allowance</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.utility_allowance ? `$${Number(unit.utility_allowance).toLocaleString()}` : '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Square Footage</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.square_footage ? `${unit.square_footage} sqft` : '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Household Size</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.household_size || '—'}
                            </span>
                          </div>

                          {/* Dates & IDs */}
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Move-in Date</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.move_in_date || '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Move-out Date</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.move_out_date || '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Lease Start</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.lease_start_date || '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Lease End</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.lease_end_date || '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Recert Due Date</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.recert_due_date || '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Tenant ID</span>
                            <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                              {unit.tenant_id || '—'}
                            </span>
                          </div>
                          <div>
                            <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Occupancy</span>
                            <span className={`capitalize ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                              {unit.occupancy_status || 'occupied'}
                            </span>
                          </div>
                          {/* Accessibility */}
                          {(unit.ada_accessible || unit.hearing_accessible || unit.vision_accessible) && (
                            <div>
                              <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Accessibility</span>
                              <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                                {[unit.ada_accessible && 'ADA', unit.hearing_accessible && 'Hearing', unit.vision_accessible && 'Vision'].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          )}
                          {/* Program Participation */}
                          {unit.program_flags && Object.keys(unit.program_flags).filter(k => unit.program_flags[k]).length > 0 && (
                            <div className="col-span-2">
                              <span className={`block font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Programs</span>
                              <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                                {Object.keys(unit.program_flags).filter(k => unit.program_flags[k]).map(k => k.replace(/_/g, ' ')).join(', ')}
                                {unit.program_designations && Object.keys(unit.program_designations).length > 0 && (
                                  <span className={`ml-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    ({Object.entries(unit.program_designations).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}%`).join(', ')})
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className={`mt-3 flex items-center justify-between border-t pt-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                          <span className={`text-[11px] italic ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                            — means data not provided, blank, or nil
                          </span>
                          <div className="flex items-center gap-2">
                            <button onClick={(e) => { e.stopPropagation(); setUnitHistoryModal(unit); }}
                              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                                isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                              }`}>
                              <Clock size={12} /> History
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleOpenEditUnit(unit); }}
                              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                                isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                              }`}>
                              <Pencil size={12} /> Edit Unit
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {filteredUnits.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {hasActiveFilters ? 'No units match the current filters.' : 'No units yet. Add units manually or upload a rent roll.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`flex items-center justify-between px-4 py-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {(currentPage - 1) * UNITS_PER_PAGE + 1}–{Math.min(currentPage * UNITS_PER_PAGE, filteredUnits.length)} of {filteredUnits.length}{hasActiveFilters ? ` (filtered from ${units.length})` : ''}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`px-2 py-1 rounded text-xs disabled:opacity-30 ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
              >Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`dots-${i}`} className={`px-1 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>...</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`w-7 h-7 rounded text-xs font-medium ${
                        currentPage === p
                          ? 'bg-blue-600 text-white'
                          : isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >{p}</button>
                  )
                )}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`px-2 py-1 rounded text-xs disabled:opacity-30 ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
              >Next</button>
            </div>
          </div>
        )}
      </div>}

      {/* Add unit modal */}
      {showAddUnit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-md rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Add Unit</h2>
            <form onSubmit={handleAddUnit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Unit Number *</label>
                <input type="text" required value={unitForm.unit_number}
                  onChange={(e) => setUnitForm(f => ({ ...f, unit_number: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                  placeholder="e.g., 101" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Bedrooms</label>
                  <input type="number" min="0" value={unitForm.bedroom_count}
                    onChange={(e) => setUnitForm(f => ({ ...f, bedroom_count: parseInt(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Designation</label>
                  <select value={unitForm.current_designation}
                    onChange={(e) => setUnitForm(f => ({ ...f, current_designation: e.target.value ? parseInt(e.target.value) : '' }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
                    <option value="">None (undesignated)</option>
                    {DESIGNATIONS.map(d => <option key={d} value={d}>{d}% AMI</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="qualified" checked={unitForm.qualified}
                    onChange={(e) => setUnitForm(f => ({ ...f, qualified: e.target.checked }))} />
                  <label htmlFor="qualified" className="text-sm text-gray-700 dark:text-gray-300">Qualified (tax credit unit)</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="initial_qualified" checked={unitForm.initial_qualified}
                    onChange={(e) => setUnitForm(f => ({ ...f, initial_qualified: e.target.checked }))} />
                  <label htmlFor="initial_qualified" className="text-sm text-gray-700 dark:text-gray-300">Initial Qualified Group</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="is_manager_unit" checked={unitForm.is_manager_unit}
                    onChange={(e) => setUnitForm(f => ({ ...f, is_manager_unit: e.target.checked }))} />
                  <label htmlFor="is_manager_unit" className="text-sm text-purple-600 dark:text-purple-400">Manager Unit</label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-3">
                <button type="button" onClick={() => setShowAddUnit(false)}
                  className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}>Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Add Unit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Unit Modal */}
      {editUnitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-lg rounded-xl p-6 max-h-[85vh] overflow-y-auto ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                Edit Unit {editUnitModal.unit_number}
              </h2>
              <div className="flex items-center gap-2">
                <label className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border cursor-pointer transition-colors ${
                  ticUploadLoading ? 'opacity-50 pointer-events-none' : ''
                } ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>
                  {ticUploadLoading ? <Loader2 size={13} className="animate-spin" /> : <FileUp size={13} />}
                  {ticUploadLoading ? 'Parsing...' : 'Upload TIC'}
                  <input type="file" className="hidden" accept=".pdf" onChange={handleTicUploadInModal} disabled={ticUploadLoading} />
                </label>
                <button onClick={() => setEditUnitModal(null)}
                  className={`p-1 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                  <XCircle size={18} className="text-gray-400" />
                </button>
              </div>
            </div>
            <form onSubmit={handleSaveEditUnit} className="space-y-4">
              {/* Income Calculation Section */}
              <div>
                <h3 className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Income Calculation</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Annual Household Income ($)</label>
                    <input type="number" step="0.01" value={editUnitForm.household_income}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditUnitForm(f => {
                          const updated = { ...f, household_income: val };
                          if (val && f.income_limit) {
                            updated.household_income_percent = (Number(val) / Number(f.income_limit) * 100).toFixed(2);
                          }
                          return updated;
                        });
                      }}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      placeholder="e.g., 35000" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                      Income Limit ($)
                    </label>
                    <div className="relative">
                      <input type="number" step="0.01" value={editUnitForm.income_limit}
                        onChange={(e) => {
                          const val = e.target.value;
                          setEditUnitForm(f => {
                            const updated = { ...f, income_limit: val };
                            if (f.household_income && val) {
                              updated.household_income_percent = (Number(f.household_income) / Number(val) * 100).toFixed(2);
                            }
                            return updated;
                          });
                        }}
                        disabled={lookupLoading}
                        className={`w-full pl-3 pr-8 py-2 rounded-lg border text-sm transition-all duration-300
                          ${lookupLoading
                            ? isDark
                              ? 'bg-blue-900/30 border-blue-500 text-blue-300 animate-pulse ring-1 ring-blue-500/40'
                              : 'bg-blue-50 border-blue-400 text-blue-600 animate-pulse ring-1 ring-blue-300'
                            : isDark
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-200 text-gray-900'
                          }`}
                        placeholder={lookupLoading ? 'Searching HUD limits...' : 'e.g., 52000'} />
                      <button type="button" onClick={handleLookupIncomeLimit}
                        disabled={lookupLoading || !editUnitModal?.current_designation}
                        title={!editUnitModal?.current_designation ? 'Unit needs a designation first' : 'AI lookup HUD income limit'}
                        className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded transition-colors
                          ${lookupLoading ? 'cursor-wait' : 'cursor-pointer'}
                          ${lookupLoading
                            ? 'text-blue-500'
                            : isDark
                              ? 'text-blue-400 hover:bg-blue-500/20'
                              : 'text-blue-500 hover:bg-blue-50'
                          }
                          disabled:opacity-30 disabled:cursor-not-allowed`}>
                        {lookupLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
                {/* Inline household size prompt for lookup */}
                {showHhSizePrompt && (
                  <div className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-orange-900/20 border border-orange-500/20' : 'bg-orange-50 border border-orange-100'}`}>
                    <span className={`text-xs whitespace-nowrap ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>Household size?</span>
                    <input type="number" min="1" max="12" autoFocus value={promptHhSize}
                      onChange={(e) => setPromptHhSize(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && promptHhSize && Number(promptHhSize) >= 1) { e.preventDefault(); runIncomeLimitLookup(Number(promptHhSize)); } }}
                      className={`w-16 px-2 py-1 rounded border text-sm text-center ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      placeholder="#" />
                    <button type="button"
                      disabled={!promptHhSize || Number(promptHhSize) < 1}
                      onClick={() => runIncomeLimitLookup(Number(promptHhSize))}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition-colors
                        ${isDark ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-600 text-white hover:bg-blue-700'}
                        disabled:opacity-40 disabled:cursor-not-allowed`}>
                      Look up
                    </button>
                    <button type="button" onClick={() => setShowHhSizePrompt(false)}
                      className={`p-0.5 rounded ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
                      <XCircle size={14} />
                    </button>
                  </div>
                )}
                {/* Auto-calculated percentage display */}
                {editUnitForm.household_income && editUnitForm.income_limit && (
                  <div className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                    Number(editUnitForm.household_income_percent) > (editUnitModal.current_designation || 60)
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                      : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                  }`}>
                    <span className="font-medium">{editUnitForm.household_income_percent}% AMI</span>
                    <span className={`text-xs ${isDark ? 'opacity-60' : 'opacity-50'}`}>
                      (auto-calculated from ${Number(editUnitForm.household_income).toLocaleString()} / ${Number(editUnitForm.income_limit).toLocaleString()})
                    </span>
                  </div>
                )}
                {/* Lookup result or helper text */}
                {lookupResult && !lookupResult.error && (
                  <div className={`mt-2 px-3 py-2 rounded-lg text-xs ${isDark ? 'bg-blue-900/20 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}>
                    <div className={`font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                      AI Income Limit: ${Number(lookupResult.income_limit).toLocaleString()}
                      {lookupResult.max_rent && <span className="ml-2">| Max Rent: ${Number(lookupResult.max_rent).toLocaleString()}</span>}
                    </div>
                    <div className={`mt-0.5 ${isDark ? 'text-blue-400/70' : 'text-blue-500'}`}>
                      {lookupResult.area_name}{lookupResult.effective_date && ` (${lookupResult.effective_date})`}
                    </div>
                    {lookupResult.source_url && (
                      <a href={lookupResult.source_url} target="_blank" rel="noopener noreferrer"
                        className={`underline mt-0.5 inline-block ${isDark ? 'text-blue-400/60' : 'text-blue-400'}`}>
                        View source
                      </a>
                    )}
                  </div>
                )}
                {lookupResult?.error && (
                  <div className={`mt-2 px-3 py-2 rounded-lg text-xs ${isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
                    {lookupResult.error}
                  </div>
                )}
                {!lookupResult && !showHhSizePrompt && (
                  <p className={`mt-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Click the <Search size={10} className="inline" /> icon on Income Limit to auto-find the HUD limit for this area and designation.
                  </p>
                )}
                {/* Manual percentage override */}
                <div className="mt-2">
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                    HH Income (% AMI) {editUnitForm.household_income && editUnitForm.income_limit ? <span className={`font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>— auto-calculated, or override manually</span> : ''}
                  </label>
                  <input type="number" step="0.1" value={editUnitForm.household_income_percent}
                    onChange={(e) => setEditUnitForm(f => ({ ...f, household_income_percent: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                    placeholder="e.g., 55.5" />
                </div>
              </div>

              {/* Tenant Details Section */}
              <div>
                <h3 className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Tenant / Household</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Household Name</label>
                    <input type="text" value={editUnitForm.tenant_name}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, tenant_name: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      placeholder="e.g., Smith, John" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Household Size</label>
                    <input type="number" min="1" value={editUnitForm.household_size}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, household_size: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      placeholder="e.g., 3" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Tenant ID</label>
                    <input type="text" value={editUnitForm.tenant_id}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, tenant_id: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      placeholder="Optional ID" />
                  </div>
                </div>
              </div>

              {/* Rent & Lease Section */}
              <div>
                <h3 className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Rent & Lease</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Rent Amount ($)</label>
                    <input type="number" step="0.01" value={editUnitForm.rent_amount}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, rent_amount: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      placeholder="e.g., 1200.00" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Lease Rent ($)</label>
                    <input type="number" step="0.01" value={editUnitForm.lease_rent}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, lease_rent: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      placeholder="Contract rent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Utility Allowance ($)</label>
                    <input type="number" step="0.01" value={editUnitForm.utility_allowance}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, utility_allowance: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      placeholder="e.g., 150.00" />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${getRelevantDateFields(editUnitForm.occupancy_status).includes('lease_start_date') ? 'text-blue-500 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      Lease Start {getRelevantDateFields(editUnitForm.occupancy_status).includes('lease_start_date') && <span className="text-blue-500">*</span>}
                    </label>
                    <input type="date" value={editUnitForm.lease_start_date}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, lease_start_date: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} ${getRelevantDateFields(editUnitForm.occupancy_status).includes('lease_start_date') ? 'ring-1 ring-blue-400' : ''}`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Lease End</label>
                    <input type="date" value={editUnitForm.lease_end_date}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, lease_end_date: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
                  </div>
                </div>
              </div>

              {/* Dates Section */}
              <div>
                <h3 className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Dates</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${getRelevantDateFields(editUnitForm.occupancy_status).includes('move_in_date') ? 'text-blue-500 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      Move-in Date {getRelevantDateFields(editUnitForm.occupancy_status).includes('move_in_date') && <span className="text-blue-500">*</span>}
                    </label>
                    <input type="date" value={editUnitForm.move_in_date}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, move_in_date: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} ${getRelevantDateFields(editUnitForm.occupancy_status).includes('move_in_date') ? 'ring-1 ring-blue-400' : ''}`} />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${getRelevantDateFields(editUnitForm.occupancy_status).includes('move_out_date') ? 'text-blue-500 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      Move-out Date {getRelevantDateFields(editUnitForm.occupancy_status).includes('move_out_date') && <span className="text-blue-500">*</span>}
                    </label>
                    <input type="date" value={editUnitForm.move_out_date}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, move_out_date: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} ${getRelevantDateFields(editUnitForm.occupancy_status).includes('move_out_date') ? 'ring-1 ring-blue-400' : ''}`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Recertification Due Date</label>
                    <input type="date" value={editUnitForm.recert_due_date}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, recert_due_date: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
                  </div>
                </div>
              </div>

              {/* Unit Config Section */}
              <div>
                <h3 className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Unit Configuration</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Unit #</label>
                    <input type="text" value={editUnitModal.unit_number} disabled
                      className={`w-full px-3 py-2 rounded-lg border text-sm opacity-60 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Bedrooms</label>
                    <input type="number" min="0" value={editUnitForm.bedroom_count}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, bedroom_count: parseInt(e.target.value) || 0 }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Sq Ft</label>
                    <input type="number" value={editUnitForm.square_footage}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, square_footage: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                      placeholder="e.g., 750" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Occupancy Status</label>
                    <select value={editUnitForm.occupancy_status}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, occupancy_status: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
                      {OCCUPANCY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end pb-1">
                    <label className={`flex items-center gap-2 cursor-pointer text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input type="checkbox" checked={editUnitForm.initial_qualified}
                        onChange={(e) => setEditUnitForm(f => ({ ...f, initial_qualified: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      Initial Qualified Group (IQG)
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="flex items-center">
                    <label className={`flex items-center gap-2 cursor-pointer text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input type="checkbox" checked={editUnitForm.is_manager_unit || false}
                        onChange={(e) => setEditUnitForm(f => ({ ...f, is_manager_unit: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                      Manager Unit
                    </label>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3 flex-wrap">
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Accessibility:</span>
                  <label className={`flex items-center gap-1.5 cursor-pointer text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <input type="checkbox" checked={editUnitForm.ada_accessible || false}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, ada_accessible: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    ADA
                  </label>
                  <label className={`flex items-center gap-1.5 cursor-pointer text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <input type="checkbox" checked={editUnitForm.hearing_accessible || false}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, hearing_accessible: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    Hearing
                  </label>
                  <label className={`flex items-center gap-1.5 cursor-pointer text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <input type="checkbox" checked={editUnitForm.vision_accessible || false}
                      onChange={(e) => setEditUnitForm(f => ({ ...f, vision_accessible: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    Vision
                  </label>
                </div>
              </div>

              {/* Program Participation Section */}
              <div>
                <h3 className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Program Participation</h3>
                <div className="flex flex-wrap gap-3">
                  {PROGRAMS.map(prog => (
                    <label key={prog.key} className={`flex items-center gap-1.5 cursor-pointer text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input type="checkbox"
                        checked={editUnitForm.program_flags?.[prog.key] || false}
                        onChange={(e) => setEditUnitForm(f => ({
                          ...f,
                          program_flags: { ...f.program_flags, [prog.key]: e.target.checked }
                        }))}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      {prog.label}
                    </label>
                  ))}
                </div>
                {/* Per-program designation dropdowns for active programs */}
                {Object.keys(editUnitForm.program_flags || {}).filter(k => editUnitForm.program_flags[k]).length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {Object.keys(editUnitForm.program_flags || {}).filter(k => editUnitForm.program_flags[k]).map(progKey => (
                      <div key={progKey} className="flex items-center gap-2">
                        <span className={`text-xs capitalize min-w-[60px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{progKey.replace(/_/g, ' ')}:</span>
                        <select
                          value={editUnitForm.program_designations?.[progKey] || ''}
                          onChange={(e) => setEditUnitForm(f => ({
                            ...f,
                            program_designations: { ...f.program_designations, [progKey]: e.target.value ? parseInt(e.target.value) : null }
                          }))}
                          className={`flex-1 px-2 py-1 rounded border text-xs ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
                          <option value="">No designation</option>
                          {DESIGNATIONS.map(d => <option key={d} value={d}>{d}% AMI</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button type="button" onClick={() => setEditUnitModal(null)}
                  className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}>Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                  <Save size={14} className="inline mr-1" /> Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Over-Income Evaluation Panel */}
      {overIncomePanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-lg rounded-xl p-6 max-h-[80vh] overflow-y-auto ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            {overIncomePanel.loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-blue-500" size={24} />
                <span className={`ml-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Evaluating correction pathways...</span>
              </div>
            ) : overIncomePanel.data && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <ShieldAlert size={18} className="text-red-500" /> Over-Income Evaluation
                  </h2>
                  <button onClick={() => setOverIncomePanel(null)}
                    className={`p-1 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                    <XCircle size={18} className="text-gray-400" />
                  </button>
                </div>

                <div className={`p-3 rounded-lg mb-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Unit</span>
                      <div className="font-medium">#{overIncomePanel.data.unit_number}</div>
                    </div>
                    <div>
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>HH Income</span>
                      <div className="font-medium text-red-500">{overIncomePanel.data.household_income_percent}% AMI</div>
                    </div>
                    <div>
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Current Designation</span>
                      <div className="font-medium">{overIncomePanel.data.current_designation}%</div>
                    </div>
                    <div>
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Building Average</span>
                      <div className={`font-medium ${overIncomePanel.data.current_average > 60 ? 'text-red-500' : 'text-green-500'}`}>
                        {overIncomePanel.data.current_average}%
                      </div>
                    </div>
                  </div>
                </div>

                {overIncomePanel.data.recommendation && (
                  <div className="mb-4 p-3 rounded-lg border-2 border-green-500/30 bg-green-50 dark:bg-green-900/10">
                    <div className="text-xs font-medium uppercase tracking-wider text-green-600 dark:text-green-400 mb-1">
                      Recommended Action
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {overIncomePanel.data.recommendation.description}
                    </div>
                    <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {overIncomePanel.data.recommendation.impact}
                    </div>
                  </div>
                )}

                <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  All Correction Pathways
                </h3>
                <div className="space-y-2">
                  {overIncomePanel.data.pathways?.map((pathway, i) => (
                    <div key={i} className={`p-3 rounded-lg border ${
                      pathway.viable
                        ? isDark ? 'border-gray-600 bg-gray-700/30' : 'border-gray-200 bg-white'
                        : isDark ? 'border-gray-700/50 bg-gray-800/50 opacity-50' : 'border-gray-100 bg-gray-50 opacity-50'
                    }`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            pathway.tier === 'A' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            pathway.tier === 'B' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            Tier {pathway.tier}
                          </span>
                          <span className={`text-xs capitalize ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {pathway.disruption} disruption
                          </span>
                        </div>
                        {pathway.viable ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">viable</span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">not viable</span>
                        )}
                      </div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{pathway.description}</div>
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{pathway.impact}</div>
                      {pathway.resulting_average && pathway.headroom !== undefined && (
                        <div className={`text-xs mt-1 ${pathway.headroom >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          Resulting average: {pathway.resulting_average}% ({pathway.headroom >= 0 ? `${pathway.headroom}% headroom` : `${Math.abs(pathway.headroom)}% over`})
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {!overIncomePanel.data.needs_correction && (
                  <div className="mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/10 text-green-700 dark:text-green-400 text-sm flex items-center gap-2">
                    <CheckCircle2 size={16} />
                    This unit is not currently flagged as over-income. No correction needed.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Action modal (redesignate, convert, swap) */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-md rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className="text-lg font-medium mb-1 text-gray-900 dark:text-white">
              {actionModal.type === 'redesignate' ? 'Redesignate Unit' :
               actionModal.type === 'to_market' ? 'Convert to Market Rate' :
               actionModal.type === 'to_qualified' ? 'Assign Designation' :
               'Swap Units'}
            </h2>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Unit {actionModal.unit.unit_number}
              {actionModal.unit.current_designation ? ` (currently ${actionModal.unit.current_designation}% AMI)` : ''}
            </p>

            <div className="space-y-3">
              {/* Designation picker (not for to_market) */}
              {actionModal.type !== 'to_market' && (
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                    {actionModal.type === 'swap' ? 'Designation for target unit' : 'New Designation'}
                  </label>
                  <div className="flex gap-1.5">
                    {DESIGNATIONS.map(d => (
                      <button key={d} type="button"
                        onClick={() => setActionForm(f => ({ ...f, designation: d }))}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                          actionForm.designation === d
                            ? `text-white ${DESIGNATION_COLORS[d]}`
                            : isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                        }`}>
                        {d}%
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Target unit for swap — filtered to like-units (same BR or sqft) */}
              {actionModal.type === 'swap' && (() => {
                const src = actionModal.unit;
                const isLikeUnit = (u) => {
                  if (src.square_footage && u.square_footage) return src.square_footage === u.square_footage;
                  return src.bedroom_count === u.bedroom_count;
                };
                const eligibleMarket = marketRateUnits.filter(isLikeUnit);
                const eligibleUndesignated = undesignatedUnits.filter(u => u.id !== src.id && isLikeUnit(u));
                return (
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Swap with like-unit ({src.bedroom_count}BR{src.square_footage ? `, ${src.square_footage} sqft` : ''})
                  </label>
                  <select value={actionForm.target_unit_id}
                    onChange={(e) => setActionForm(f => ({ ...f, target_unit_id: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
                    <option value="">Select unit...</option>
                    {eligibleMarket.map(u => (
                      <option key={u.id} value={u.id}>Unit {u.unit_number} (Market Rate, {u.bedroom_count}BR{u.square_footage ? `, ${u.square_footage}sqft` : ''})</option>
                    ))}
                    {eligibleUndesignated.map(u => (
                      <option key={u.id} value={u.id}>Unit {u.unit_number} (Undesignated, {u.bedroom_count}BR{u.square_footage ? `, ${u.square_footage}sqft` : ''})</option>
                    ))}
                  </select>
                  {eligibleMarket.length === 0 && eligibleUndesignated.length === 0 && (
                    <p className="text-xs text-orange-500 mt-1">No eligible like-units available for swap.</p>
                  )}
                </div>
                );
              })()}

              {/* Reason */}
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Reason</label>
                <select value={actionForm.reason}
                  onChange={(e) => setActionForm(f => ({ ...f, reason: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
                  {REASONS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Notes (optional)</label>
                <textarea value={actionForm.notes}
                  onChange={(e) => setActionForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button onClick={() => setActionModal(null)}
                className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}>Cancel</button>
              <button onClick={handleAction}
                disabled={actionModal.type === 'swap' && !actionForm.target_unit_id}
                className={`px-4 py-2 rounded-lg text-sm text-white disabled:opacity-50 ${
                  actionModal.type === 'to_market' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}>
                {actionModal.type === 'redesignate' ? 'Redesignate' :
                 actionModal.type === 'to_market' ? 'Convert to Market Rate' :
                 actionModal.type === 'to_qualified' ? 'Assign Designation' :
                 'Swap Units'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Link Modal */}
      {shareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-md rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Link size={18} className="text-blue-500" />
                <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Share Snapshot</h2>
              </div>
              <button onClick={() => setShareModal(null)}
                className={`p-1 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <XCircle size={18} className="text-gray-400" />
              </button>
            </div>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Anyone with this link can view the snapshot without logging in.
            </p>
            <div className={`flex items-center gap-2 p-3 rounded-lg border ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <input
                type="text"
                readOnly
                value={shareModal.url}
                className={`flex-1 text-sm bg-transparent border-none outline-none truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(shareModal.url);
                  toast.success('Link copied to clipboard');
                }}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 shrink-0 transition-colors"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
            {shareModal.expiresAt && (
              <p className={`text-xs mt-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                This link expires on {new Date(shareModal.expiresAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Simulation Apply Confirmation Modal */}
      {simConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-md rounded-xl p-6 max-h-[80vh] overflow-y-auto ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center gap-2 mb-4">
              <FlaskConical size={18} className="text-indigo-500" />
              <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Apply Simulation Changes</h2>
            </div>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              The following changes will be applied to the actual unit records. This cannot be undone.
            </p>

            {simChanges.size > 0 && (
              <div className="mb-3">
                <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Designation Changes ({simChanges.size})
                </div>
                <div className={`rounded-lg border divide-y ${isDark ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-200'}`}>
                  {Array.from(simChanges.entries()).map(([unitId, newDes]) => {
                    const unit = units.find(u => u.id === unitId);
                    return (
                      <div key={unitId} className={`flex items-center justify-between px-3 py-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <span className="font-medium">#{unit?.unit_number}</span>
                        <span>
                          <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{unit?.current_designation}%</span>
                          {' → '}
                          <span className="font-semibold">{newDes}%</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {simOccupancyChanges.size > 0 && (
              <div className="mb-3">
                <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Occupancy Changes ({simOccupancyChanges.size})
                </div>
                <div className={`rounded-lg border divide-y ${isDark ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-200'}`}>
                  {Array.from(simOccupancyChanges.entries()).map(([unitId, newStatus]) => {
                    const unit = units.find(u => u.id === unitId);
                    return (
                      <div key={unitId} className={`flex items-center justify-between px-3 py-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <span className="font-medium">#{unit?.unit_number}</span>
                        <span>
                          <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{unit?.occupancy_status || 'occupied'}</span>
                          {' → '}
                          <span className="font-semibold capitalize">{newStatus}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {simResult?.after && (
              <div className={`rounded-lg p-3 mb-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Impact
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Average Income</span>
                  <span>
                    <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{simResult.before?.average_income}%</span>
                    {' → '}
                    <span className={`font-bold ${simResult.after.average_income <= 60 ? 'text-green-500' : 'text-red-500'}`}>
                      {simResult.after.average_income}%
                    </span>
                    {simResult.delta != null && (
                      <span className={`ml-1 text-xs ${simResult.delta < 0 ? 'text-green-500' : simResult.delta > 0 ? 'text-red-500' : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
                        ({simResult.delta > 0 ? '+' : ''}{simResult.delta}%)
                      </span>
                    )}
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setSimConfirmModal(false)}
                className={`text-sm px-4 py-2 rounded-lg border transition-colors ${
                  isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                }`}>
                Cancel
              </button>
              <button onClick={handleSimApply}
                className="text-sm px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">
                Apply {simChanges.size + simOccupancyChanges.size} Change{(simChanges.size + simOccupancyChanges.size) !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unit History Modal */}
      {unitHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setUnitHistoryModal(null)}>
          <div className={`mx-4 w-full max-w-lg max-h-[80vh] flex flex-col rounded-lg shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between px-5 py-4 border-b shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="font-medium text-sm">Unit {unitHistoryModal.unit_number} — History</h3>
              <button onClick={() => setUnitHistoryModal(null)} className={`p-1 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <AitHistoryTimeline scope="unit" ids={{ buildingId, unitId: unitHistoryModal.id }} />
            </div>
          </div>
        </div>
      )}

      <AitChatDrawer
        propertyId={propertyId}
        propertyName={building?.name || building?.bin_number}
        buildingId={buildingId}
        isOpen={showChat}
        onClose={() => setShowChat(false)}
      />
    </div>
    </div>
  );
}

export default AitBuildingDetail;
